import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from './VideoRoom.module.css';
import VideoControls from '../components/video/VideoControls';
import RoomHeader from '../components/RoomHeader';
import Chat from '../components/chat/Chat';
import MembersList from '../components/memberslist/MembersList';
import SnackBar from '../components/SnackBar';
import RecordingHeader from '../components/video/RecordingHeader';
import { WebRTCIssueStatusProvider } from '../contexts/WebRTCIssueContext';
import logger from '../utils/logger';
import { createSnack } from '../actions/SnackActions';
import { track } from '../actions/TrackActions';
import { debounce } from '../utils/functions';
import { useSelector, useDispatch } from 'react-redux';
import { ALLOW_VIDEO_MANIPULATION } from '../utils/media';
import DisconnectedModal from '../components/video/DisconnectedModal';
import Sidebar from '../components/Sidebar';
import VideoPeers from './VideoPeers';
import { CircularProgress } from '@mui/material';
import { toggleMuted, setMicLoading } from '../store/slices/controlSlice';
import {
  setFloatSelf,
  setNumPages,
  setMaxHeight,
  setMaxWidth,
  setViewLayout,
  setShowChat,
  setShowMembersList,
} from '../store/slices/layoutSlice';

import { setPeersSortMap } from '../store/slices/memberSlice';
import { ConnectionState } from '../store/slices/connectionSlice';
import { withRoomContext } from '../lib/RoomClientContext.js';

import DuplicatePeerModal from '../components/video/DuplicatePeerModal.js';
import VideoSettingsDialog from '../components/video/settings/VideoSettingsDialog';
import { useCheckPermission } from '../hooks/useCheckPermission.js';
import useConnection from '../hooks/useConnection.js';
import { setRoomId } from '../store/slices/roomSlice.js';

function VideoRoom({ roomClient }) {
  const dispatch = useDispatch();
  const clientOptions = useSelector((state) => state.user.clientOptions);
  const token = useSelector((state) => state.user.token);
  const role = useSelector((state) => state.user.role);
  const userId = useSelector((state) => state.user.userId);
  const hideVideo = useSelector((state) => state.control.hideVideo);
  const muted = useSelector((state) => state.control.muted);
  const peerJoined = useSelector((state) => state.status.peerJoined);
  const forcedReload = useSelector((state) => state.status.forcedReload);
  const viewLayout = useSelector((state) => state.layout.viewLayout);
  const dominantSpeaker = useSelector((state) => state.layout.dominantSpeaker);
  const floatShare = useSelector((state) => state.layout.floatShare);
  const floatSelf = useSelector((state) => state.layout.floatSelf);
  const pinned = useSelector((state) => state.layout.pinned);
  const page = useSelector((state) => state.layout.page);
  const showChat = useSelector((state) => state.layout.showChat);
  const showMembersList = useSelector((state) => state.layout.showMembersList);
  const members = useSelector((state) => state.member.members);
  const peersSortMap = useSelector((state) => state.member.peersSortMap);
  const raisedHands = useSelector((state) => state.member.raisedHands);
  const deviceSettings = useSelector((state) => state.device.deviceSettings);
  const showSettings = useSelector((state) => state.device.showSettings);
  const deviceSetup = useSelector((state) => state.device.deviceSetup);
  const connectionState = useSelector(
    (state) => state.connection.connectionState
  );
  const peerReconnecting = useSelector(
    (state) => state.connection.peerReconnecting
  );
  const peers = useSelector((state) => state.room.peers);
  const joinedRoom = useSelector((state) => state.room.joinedRoom);
  const showNPS = useSelector((state) => state.room.showNPS);
  const shareData = useSelector((state) => state.room.shareData);
  const roomId = useSelector((state) => state.room.roomId);
  const maxWidth = useSelector((state) => state.layout.maxWidth);
  const maxHeight = useSelector((state) => state.layout.maxHeight);
  const isDuplicatePeer = useSelector((state) => state.room.isDuplicatePeer);
  const isRoomLockEnabled = useSelector(
    (state) => state.room.isRoomLockEnabled
  );
  const roomData = useSelector((state) => state.room.roomData);
  const enableMetricsCollection = useSelector(
    (state) => state.room.enableMetricsCollection
  );

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [usersetFloatSelf, setUsersetFloatSelf] = useState(false);

  const isMobile = maxWidth <= 780 || maxHeight <= 520;
  const retryDelayMilliseconds = 5000;

  useCheckPermission(roomClient.streamHandler, roomClient.device);
  useConnection();

  useEffect(() => {
    const v = roomClient.videoLayouts;
    if (!v) return;
    v.setSelfId(userId);
    v.setPins(pinned);
    v.setHands(raisedHands);
    v.setActiveSpeaker(dominantSpeaker?.peerId);
    v.setShareData(shareData);
    v.setFloatShare(floatShare);
    v.setFloatSelf(floatSelf);
    v.setPeers(members.filter((x) => x.joined && !x.isGhost).map((x) => x.id));
    v.setRenderSize(viewLayout === 'grid' ? (isMobile ? 6 : 9) : 4);
    v.createRenderList(page);
    dispatch(setNumPages(v.getNumPages()));
    dispatch(setPeersSortMap(v.getOrderMap()));
  }, [
    joinedRoom,
    isMobile,
    members,
    userId,
    pinned,
    dominantSpeaker,
    shareData,
    floatShare,
    floatSelf,
    raisedHands,
    viewLayout,
    page,
    dispatch,
  ]);

  useEffect(() => {
    if (clientOptions?.disableChat) {
      dispatch(setShowChat(false));
    }
    if (clientOptions?.disableMembersList) {
      dispatch(setShowMembersList(false));
    }
    if (
      clientOptions?.disableVisualEffects &&
      roomClient.videoManipulator &&
      deviceSettings
    ) {
      roomClient.onNewDeviceSettings({
        ...deviceSettings,
        videoEffect: null,
      });
    }
  }, [clientOptions, deviceSettings]);

  useEffect(() => {
    if (roomClient.members.length) {
      const m = roomClient.members.reduce((acc, cur) => {
        acc[cur.id] = cur;
        return acc;
      }, {});

      const joined = members.reduce((acc, cur) => {
        if (!m[cur.id] && cur.id !== userId && cur.joined) {
          acc.push(cur);
        }
        return acc;
      }, []);

      const left = roomClient.members.reduce((acc, cur) => {
        const found = members.find((m) => m.id === cur.id);
        if ((!found || found.isGhost) && cur.id !== userId && !cur.isGhost) {
          acc.push(cur);
        }
        return acc;
      }, []);

      if (joined.length > 0) {
        if (joined.length === 1) {
          createSnack(
            dispatch,
            `${joined[0].userData?.meetingName || t('Unknown')} ${t(
              'joined the room'
            )}`,
            'info'
          );
        } else {
          createSnack(
            dispatch,
            `${joined.length} ${t('users joined the room')}`,
            'info'
          );
        }
      }

      if (left.length > 0) {
        if (left.length === 1) {
          createSnack(
            dispatch,
            `${left[0].userData?.meetingName || t('Unknown')} ${t(
              'left the room'
            )}`,
            'info'
          );
        } else {
          createSnack(
            dispatch,
            `${left.length} ${t('users left the room')}`,
            'info'
          );
        }
      }
    }
    roomClient.members = members;
  }, [members, userId, dispatch, t]);

  const checkForSinglePeer = debounce((numPeers) => {
    logger.log('number of peers left', numPeers);
    if (usersetFloatSelf) return;
    if (numPeers >= 1) {
      dispatch(setFloatSelf(true));
    }
  }, 100);

  useEffect(() => {
    (async () => {
      if (!roomClient.audioProducer) return;
      dispatch(setMicLoading(true));
      await roomClient.toggleAudio();
      dispatch(setMicLoading(false));
      await track('audioMutedToggled', { muted }, token);
    })();
  }, [muted, dispatch, roomClient, token]);

  useEffect(() => {
    if (!deviceSetup) return;
    if (!joinedRoom) return;
    debounce(() => {
      roomClient.onToggleVideo();
    }, 250)();
  }, [hideVideo, deviceSetup, joinedRoom, roomClient]);

  const onResize = debounce(() => {
    dispatch(setMaxWidth(window.innerWidth));
    dispatch(setMaxHeight(window.innerHeight));
    if (window.innerWidth <= 768) {
      dispatch(setViewLayout('grid'));
      roomClient.preShareLayout = null;
    }
  });

  useEffect(() => {
    if (!enableMetricsCollection) return;
    const t = setInterval(async () => {
      const producerStats = await roomClient.producerTransport?.getStats();
      const outbounds = Array.from(producerStats.values()).filter(
        (x) => x.type === 'outbound-rtp'
      );
      const consumerStats = await roomClient.consumerTransport?.getStats();
      const inbounds = Array.from(consumerStats.values()).filter(
        (x) => x.type === 'inbound-rtp' && x.mid !== 'probator'
      );
      await track('rtp-stats', { inbounds, outbounds }, token);
    }, 10000);
    return () => {
      clearInterval(t);
    };
  }, [enableMetricsCollection, roomClient, token]);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);

  let peersCombined = useMemo(() => {
    logger.log('peers Object is', peers);
    let peersArray = [];
    Object.values(peers).forEach((peer) => {
      if (peer['audio']) {
        peersArray.push(peer['audio']);
      }
      if (peer['video']) {
        peersArray.push(peer['video']);
      }
    });
    logger.log('peersArray is', peersArray);
    const y = peersArray.reduce((acc, x) => {
      if (x.producer.id === shareData?.producerId) return acc;
      if (!acc[x.peerId]) {
        acc[x.peerId] = {
          id: x.peerId,
          producers: [],
          show: false,
        };
      }
      acc[x.peerId].producers.push(x);
      if (x.show) {
        acc[x.peerId].show = true;
      }
      return acc;
    }, {});

    (members || []).forEach((member) => {
      if (!y[member.id] && member.id !== userId && !member.isGhost) {
        y[member.id] = {
          id: member.id,
          producers: [],
          show: true,
        };
      }
    });

    return Object.values(y).sort((a, b) => {
      return peersSortMap[a.id] - peersSortMap[b.id];
    });
  }, [members, peers, shareData, peersSortMap, userId]);

  useEffect(() => checkForSinglePeer(peersCombined.length), [peersCombined]);

  const useSwipeableDrawer = maxWidth <= 1050 || isMobile;
  const renderSize = roomClient.videoLayouts.renderSize;

  const numPages = Math.ceil(
    (peersCombined.length +
      (shareData && !floatShare ? 1 : 0) +
      (floatSelf ? 0 : 1)) /
      renderSize
  );

  useEffect(() => {
    if (roomClient.closed) {
      return;
    }

    if (forcedReload) {
      return;
    }

    const onUserConfirmsLeave = () => {
      roomClient.close();
    };

    const handleBeforeLeave = (e) => {
      const confirmationMessage =
        'Are you sure you want to leave? Changes you made may not be saved.';
      e.preventDefault();
      e.returnValue = confirmationMessage; // This is necessary for the confirmation dialog to appear

      return confirmationMessage;
    };

    window.addEventListener('beforeunload', handleBeforeLeave);
    window.addEventListener('pagehide', onUserConfirmsLeave);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeLeave);
      window.removeEventListener('pagehide', onUserConfirmsLeave);
    };
  }, [roomClient.closed, forcedReload]);

  useEffect(() => {
    if (forcedReload) window.location.reload();
  }, [forcedReload]);

  useEffect(() => {
    if (showNPS) {
      if (showNPS === 'kicked') {
        navigate('/kicked');
      } else if (showNPS === 'closed') {
        navigate('/closed');
      } else {
        navigate('/bye');
      }
    }
  }, [showNPS, navigate]);

  const allowVideoManipulation =
    (ALLOW_VIDEO_MANIPULATION || clientOptions?.forceVirtualBackgroundUrl) &&
    !clientOptions?.disableVisualEffects;

  // Memoize the props for RoomHeader to prevent re-renders when unrelated state changes
  const roomHeaderProps = useMemo(
    () => ({
      roomName: roomData?.name || (roomData?.useLobby ? roomId : null),
      startedAt: roomData?.startedAt,
      numPeers: peersCombined.length + 1,
      useLobby: role === 'admin' && roomData?.useLobby,
      hideRoomLock:
        !isRoomLockEnabled || !(role === 'admin' || role === 'moderator'),
    }),
    [
      roomData?.name,
      roomData?.useLobby,
      roomData?.startedAt,
      roomId,
      peersCombined.length,
      role,
      isRoomLockEnabled,
    ]
  );

  if (connectionState === ConnectionState.RECONNECTING) {
    function reloadWindow() {
      window.location.reload();
    }
    return (
      <div className={styles.container}>
        {peerReconnecting ||
        connectionState === ConnectionState.RECONNECTING ? (
          <DisconnectedModal
            connectionFailure={connectionState === ConnectionState.RECONNECTING}
            onClick={reloadWindow}
            retryDelayMilliseconds={retryDelayMilliseconds}
          />
        ) : null}
      </div>
    );
  }

  if (isDuplicatePeer) {
    return (
      <div className={styles.container}>
        <DuplicatePeerModal />
      </div>
    );
  }

  return (
    <>
      <SnackBar leftCollapse={!useSwipeableDrawer && showMembersList} />
      <div className={styles.container}>
        <Helmet>
          <title>
            {t('Video Meeting')}
            {roomData?.name ? ` - ${roomData.name}` : ''}
          </title>
        </Helmet>
        {peerJoined && !clientOptions.disableMembersList && showMembersList && (
          <Sidebar useSwipeableDrawer={useSwipeableDrawer} anchor="left">
            <MembersList />
          </Sidebar>
        )}
        <div
          className={classnames(styles.innerContainer, {
            [styles.leftCollapse]:
              !useSwipeableDrawer && peerJoined && showMembersList,
            [styles.rightCollapse]:
              !useSwipeableDrawer && peerJoined && showChat,
          })}
          style={{ height: maxHeight }}
        >
          <RoomHeader {...roomHeaderProps} />
          <RecordingHeader />
          <VideoPeers
            numPages={numPages}
            roomId={roomId}
            isMobile={isMobile}
            setUsersetFloatSelf={setUsersetFloatSelf}
            peersCombined={peersCombined}
            renderSize={renderSize}
            allowVideoManipulation={allowVideoManipulation}
            onPeerMute={(peer) => roomClient.onPeerMute(peer)}
            onToggleMute={() => dispatch(toggleMuted())}
          />
          {peerJoined && <VideoControls />}
          {showSettings && <VideoSettingsDialog />}
          {connectionState !== ConnectionState.CONNECTED && (
            <div className={styles.settingsLoadingContainer}>
              <CircularProgress />
              <h3>{t('Loading meeting settings...')}</h3>
            </div>
          )}
        </div>
        {peerJoined && !clientOptions.disableChat && showChat && (
          <Sidebar useSwipeableDrawer={useSwipeableDrawer} anchor="right">
            <Chat />
          </Sidebar>
        )}
      </div>
    </>
  );
}

const VideoRoomWrapper = ({ roomClient }) => {
  const { roomId } = useParams();
  const dispatch = useDispatch();
  dispatch(setRoomId(roomId));

  return (
    <WebRTCIssueStatusProvider roomId={roomId} roomClient={roomClient}>
      <VideoRoom roomClient={roomClient} />
    </WebRTCIssueStatusProvider>
  );
};

export default withRoomContext(VideoRoomWrapper);
